import { isSuccessMessage, MsgPayloadType } from '@halo/chat-sdk';
import type { Client, Msg, User } from '@halo/chat-sdk-react';
import { type SessionStoreInstance } from '@halo/chat-sdk-react';
import { useBoolean } from 'ahooks';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import Lightbox, { type ControllerRef, type ZoomRef } from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { useStoreEnhance } from '@halo/zustand-enhance';
import type { Slide } from 'yet-another-react-lightbox';
import { tools } from '@halo/ui-chat-react/utils';
import { useTranslation } from 'react-i18next';
import { AutoClose, KeepIndex, PrevNext, Zoom } from '../../LightboxPlugin';
import { ImagePreviewPlugin } from './Plugin';

export interface ImagePreviewRef {
  open: (
    message: Msg,
    store: {
      sessionStoreInstance?: SessionStoreInstance;
      client?: Client;
      user?: User;
    }
  ) => void;
  close: () => void;
}

export const ImagePreview = forwardRef<ImagePreviewRef>(function ImagePreview(_, ref) {
  const { t } = useTranslation();
  const [open, { setTrue, setFalse }] = useBoolean(false);
  const [sessionStoreInstance, setSessionStoreInstance] = useState<SessionStoreInstance>();
  const [client, setClient] = useState<Client>();
  const [user, setUser] = useState<User>();
  const [initMessage, setInitMessage] = useState<Msg>();

  const sessionStore = useStoreEnhance(sessionStoreInstance);

  const controllerRef = useRef<ControllerRef>(null);

  const slides = useMemo(() => {
    const slides: Slide[] = [];
    sessionStore?.enhancedMessages.forEach((message) => {
      if (message.payload_type !== MsgPayloadType.IMAGE) return;
      if (!isSuccessMessage(message.status)) return;
      const src = message.payload?.thumbnail;

      slides.push({
        key: message.cli_msg_id,
        src: src,
        width: message.payload?.width,
        height: message.payload?.height,
        message: message,
        type: 'image' as const,
      });
    });
    return slides;
  }, [sessionStore?.enhancedMessages]);

  const [index, setIndex] = useState<number>(0);
  useEffect(() => {
    if (!slides.length || !initMessage) {
      return;
    }
    setInitMessage(undefined);

    const index = slides.findIndex((_) => _.message?.cli_msg_id === initMessage.cli_msg_id);

    setIndex(index);
  }, [slides, initMessage]);

  useImperativeHandle(
    ref,
    () => ({
      open: (message, store) => {
        if (!isSuccessMessage(message.status)) return;
        const { sessionStoreInstance, client, user } = store;
        setSessionStoreInstance(sessionStoreInstance);
        setClient(client);
        setUser(user);
        setInitMessage(message);
        setTrue();
      },
      close: () => {
        setFalse();
      },
    }),
    [setTrue, setFalse]
  );

  const zoomRef = useRef<ZoomRef>(null);

  const handleImagePreviewZoomIn = useCallback(() => {
    zoomRef.current?.zoomIn();
  }, [zoomRef]);

  const handleImagePreviewZoomOut = useCallback(() => {
    zoomRef.current?.zoomOut();
  }, []);

  useEffect(() => {
    if (!open) {
      setSessionStoreInstance(undefined);
      setInitMessage(undefined);
    }
  }, [open]);

  const [disabled, setDisabled] = useState<boolean>(true);

  const handleAutoClose = useCallback(
    (type: 'image' | 'video') => {
      if (type === 'image') {
        tools.toaster.warning({ title: t('tips.imagePreviewAutoCloseImage') });
      }
    },
    [t]
  );

  return (
    <Lightbox
      index={index}
      open={open}
      close={setFalse}
      slides={slides}
      on={{
        imagePreviewZoomIn: handleImagePreviewZoomIn,
        imagePreviewZoomOut: handleImagePreviewZoomOut,
        entered: () => {
          setDisabled(false);
        },
        exited: () => {
          setDisabled(true);
        },
        entering: () => {
          setDisabled(true);
        },
        exiting: () => {
          setDisabled(true);
        },
        autoClose: handleAutoClose,
      }}
      controller={{ ref: controllerRef, closeOnBackdropClick: true }}
      plugins={[ImagePreviewPlugin, Zoom, PrevNext, KeepIndex, AutoClose]}
      carousel={{ imageFit: 'contain', finite: true }}
      zoom={{
        ref: zoomRef,
        maxZoomPixelRatio: 5,
        minZoom: 0.1,
        zoomInMultiplier: 1.15,
        scrollToZoom: true,
        wheelZoomStepRatio: 0.1,
        disabled,
      }}
      imagePreview={{ sessionStore, client, user }}
      styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }}
      toolbar={{ buttons: [] }}
      autoClose={{ enabled: true }}
    />
  );
});
